// Catalog.ID Client — The default AUTH token's device cache (D12, §4.12)
// ============================================================================
// The account's default spending token (`catalog:default`, payee `operator`) is
// minted once at registration — UNBOUND, scoped to Catalog.ID's service types
// (apps/docs/plans/second-node-and-bitcash-open-issues.md §2.1, §9.3) — and then
// handed to every Catalog service, at any operator, that asks through the
// key-grant flow. Asking for the supersecret on each of
// those requests would be theatre — the token is already a bounded, revocable
// authorisation, and the member approved it in the consent dialog — so it is
// CACHED on the device.
//
// What that cache is, and what it is NOT:
//
//   * NOT a key-bag entry. The wallet KEY is bag-only and never device-stored
//     (D5); the token is a different thing entirely — an authorisation with
//     caps, revocable from Settings and from the wallet manager.
//   * NOT the truth. The Worker's token list is (§10). The cache is dropped on
//     any refusal — a module reporting 403 `revoked`/`expired`/`token_scope`, or
//     a revoke in Settings — and the next request refills it or mints a new token.
//   * SCOPED. An entry records the scope the token was issued with: unbound, and
//     its service types. An entry that does not say both is from before §9.3 (a
//     token bound to one Operator's receive address) and reads as empty; one whose
//     types no longer cover the configured list is dropped by the handler
//     ({@link cachedTokenServes}). Either way the next grant goes to the ledger,
//     which repairs the token.
//   * NOT printed and NOT in a bundle.
//
// SLOT NAMES CARRY THE CID. The plan names the slots `bitcash_default_token`
// and `bitcash_org_default_token:<orgCid>`; both are prefixed here with the
// owning account's cid, exactly as `state/device-storage.ts` namespaces every
// device key by `{cid}_…`. Without that prefix, switching identity on a shared
// browser would read — and release to a module — the PREVIOUS account's
// spending token. One prefix closes it.
//
// STORAGE IS INJECTED, and the injected implementation is the flows track's
// `state/device-slots.ts` — AES under the device DEK, PIN-unlocked, swept by
// *Forget this device*, and (the part that matters here) NEVER throwing: an
// absent slot, a stale DEK, "no local storage" and a quota refusal all read as
// null and write as false, which is precisely §4.12's "drop the cache on any
// refusal". This module keeps the interface anyway, because the handler must be
// testable without storage, and because a cache that cannot be installed must
// degrade to "always refill" rather than to a broken grant.

import { walletKind } from '../crypto/bitcash';
import {
    DEFAULT_TOKEN_SLOT, orgDefaultTokenSlot as deviceOrgDefaultTokenSlot,
    getDeviceSlot, setDeviceSlot, clearDeviceSlot,
} from '../state/device-slots';

/** The account's own default token. */
export function defaultTokenSlot(cid: string): string {
    return `${cid}/${DEFAULT_TOKEN_SLOT}`;
}

/** An organization's default token, one slot per organization (§4.13). */
export function orgDefaultTokenSlot(cid: string, orgCid: string): string {
    return `${cid}/${deviceOrgDefaultTokenSlot(orgCid)}`;
}

/** Split a slot name back into the identity that owns it and the device-slot
 *  name. Pure, and the one place the `<cid>/<name>` convention is spelled out;
 *  a name with no cid half belongs to no identity and is refused by every
 *  caller rather than being written under whoever happens to be active. */
export function splitSlot(slot: string): { cid: string; name: string } | null {
    const cut = slot.indexOf('/');
    if (cut <= 0 || cut === slot.length - 1) return null;
    return { cid: slot.slice(0, cut), name: slot.slice(cut + 1) };
}

/**
 * The storage contract. One string per slot; the implementation answers for
 * encryption (device DEK, PIN-unlocked) and for clearing on *Forget this
 * device*.
 */
export interface DefaultTokenCache {
    get(slot: string): Promise<string | null>;
    set(slot: string, token: string): Promise<void>;
    clear(slot: string): Promise<void>;
}

/** What a slot holds. The walletRef and the expiry ride along so a cache hit
 *  can answer the grant in full without a Worker round-trip; the scope rides
 *  along so a hit is never a token the configuration has outgrown. */
export interface CachedDefaultToken {
    /** The 34-character display form of the AUTH token. */
    token: string;
    /** The non-spendable handle of the wallet it debits, when known. */
    walletRef?: string;
    /** `catalog:default` — carried so a future label change is visible. */
    label: string;
    /** Unix ms, or null for a non-expiring token. */
    expiresAt: number | null;
    /** The service types the token was issued (or last widened) with. */
    serviceTypes: string[];
    /** Always true: the default token names no payee (§9.3). An entry without it predates that. */
    unbound: true;
}

/** A bare v2 service type (D55), as `caps.serviceTypes` takes it. */
const SERVICE_TYPE = /^[a-z][a-z0-9-]{0,31}$/;

export function encodeCachedToken(entry: CachedDefaultToken): string {
    const out: CachedDefaultToken = {
        token: entry.token,
        label: entry.label,
        expiresAt: entry.expiresAt ?? null,
        serviceTypes: [...entry.serviceTypes],
        unbound: true,
    };
    if (entry.walletRef) out.walletRef = entry.walletRef;
    return JSON.stringify(out);
}

/**
 * The slot value for a default token a registration wizard has just minted: what the handler
 * decodes and serves — the token, the wallet's ref, and the scope it was minted with (unbound,
 * the node's service types). Never the wallet key: a slot holds a spending AUTHORISATION only.
 */
export function mintedDefaultTokenEntry(args: { token: string; walletRef?: string; serviceTypes: readonly string[] }): string {
    return encodeCachedToken({
        token: args.token,
        ...(args.walletRef ? { walletRef: args.walletRef } : {}),
        label: 'catalog:default',
        expiresAt: null,
        serviceTypes: [...args.serviceTypes],
        unbound: true,
    });
}

/** True when the entry's scope covers every configured service type — the only
 *  entry a grant may be served from. An empty configuration serves nothing. */
export function cachedTokenServes(entry: CachedDefaultToken, configured: readonly string[]): boolean {
    if (configured.length === 0) return false;
    const held = new Set(entry.serviceTypes);
    return configured.every(t => held.has(t));
}

/**
 * Read a slot back. Every structural promise is re-checked, and anything that
 * fails one returns null rather than throwing: a corrupt cache must degrade
 * into "cold cache", never into a failed grant. In particular the value must be
 * an AUTH token — a wallet key that ended up here by a bug is a SPENDING KEY,
 * and releasing it to a module would hand over the wallet itself.
 */
export function decodeCachedToken(raw: string | null): CachedDefaultToken | null {
    if (!raw) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    const o = parsed as Record<string, unknown>;
    if (typeof o.token !== 'string' || walletKind(o.token) !== 'auth') return null;
    if (typeof o.label !== 'string') return null;
    const expiresAt = o.expiresAt === null || o.expiresAt === undefined
        ? null
        : (typeof o.expiresAt === 'number' && Number.isFinite(o.expiresAt) ? o.expiresAt : undefined);
    if (expiresAt === undefined) return null;
    // The scope it was issued with (§9.3): unbound, and a list of service types. Without
    // both it is a token from before — bound, or unscoped — and the cache is cold.
    if (o.unbound !== true) return null;
    const types = o.serviceTypes;
    if (!Array.isArray(types) || types.length === 0
        || !types.every((t): t is string => typeof t === 'string' && SERVICE_TYPE.test(t))) return null;
    const entry: CachedDefaultToken = { token: o.token, label: o.label, expiresAt, serviceTypes: [...types], unbound: true };
    if (typeof o.walletRef === 'string' && o.walletRef) entry.walletRef = o.walletRef;
    return entry;
}

/**
 * The real cache: the device slots of `state/device-slots.ts`, under the device
 * DEK. The identity half of the slot name is passed explicitly rather than left
 * to the module's "active cid" default — a grant that began under one identity
 * must finish under that identity even if the member switched tabs mid-dialog.
 */
export class DeviceDefaultTokenCache implements DefaultTokenCache {
    async get(slot: string): Promise<string | null> {
        const parts = splitSlot(slot);
        if (!parts) return null;
        return await getDeviceSlot<string>(parts.name, parts.cid);
    }

    async set(slot: string, token: string): Promise<void> {
        const parts = splitSlot(slot);
        if (!parts) return;
        // `false` means this device will ask again — never a failure of the
        // grant the token was minted for.
        await setDeviceSlot(parts.name, token, parts.cid);
    }

    async clear(slot: string): Promise<void> {
        const parts = splitSlot(slot);
        if (!parts) return;
        clearDeviceSlot(parts.name, parts.cid);
    }
}

/** Storage-free fallback, used by tests and by any build where device slots are
 *  unavailable. Session-scoped: a reload costs one more supersecret prompt. */
export class MemoryDefaultTokenCache implements DefaultTokenCache {
    private readonly slots = new Map<string, string>();

    async get(slot: string): Promise<string | null> {
        return this.slots.get(slot) ?? null;
    }

    async set(slot: string, token: string): Promise<void> {
        this.slots.set(slot, token);
    }

    async clear(slot: string): Promise<void> {
        this.slots.delete(slot);
    }
}

let installed: DefaultTokenCache | null = null;

/** Install the process-wide cache. The key-grant client installs the fallback
 *  at start-up; the flows track replaces it with the device-DEK implementation. */
export function setDefaultTokenCache(cache: DefaultTokenCache | null): void {
    installed = cache;
}

export function getDefaultTokenCache(): DefaultTokenCache | null {
    return installed;
}

/**
 * Drop one slot — the invalidation half of §4.12. Called from Settings when the
 * member revokes the default token, and by the handler when a module reports
 * that the token it was given was refused. Never throws: a cache that cannot be
 * cleared must not break the revoke it is a side-effect of.
 */
export async function invalidateDefaultTokenCache(slot: string): Promise<void> {
    try {
        await installed?.clear(slot);
    } catch {
        // A cache that cannot be cleared is a cache that will miss — acceptable.
    }
}
